import React, { useMemo } from "react";
import type { FormikErrors } from "formik";
import { type FormikFormChildProps } from "../FormikForm/FormikFormWrapperForJetpackConfig";
import { Config, DEFAULT_LEADERBOARD_TOKEN_AMOUNT } from "kadow-jetpack-model";
import KC_UIText from "../../../../designsystem/typography/KC_UIText/KC_UIText";
import { KC_Weights } from "../../../../designsystem/ds_commons";
import { tt } from "../../../../utils/i18n/i18n";
import KC_Button, {
  KC_ButtonSize
} from "../../../../designsystem/KC_Button/KC_Button";
import TwemojiWrapper from "../../../../components/TwemojiWrapper/TwemojiWrapper";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import NiceModal from "@ebay/nice-modal-react";
import GiftSuggestionModal from "../../../../components/modal/Onboarding/GiftSuggestion/GiftSuggestionModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type {
  LeaderboardSpecialPrize,
  LoyaltyTierPrize
} from "kadow-jetpack-model";
import { buildDefaultTierPrize } from "../Loyalty/helper/defaultLoyaltyTiers";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import LoyaltyPrizeRewardEditor from "./component/LoyaltyPrizeRewardEditor";
import {
  LoyaltyRewardCardLayout,
  LoyaltyRewardEditorPageLayout
} from "./component/LoyaltyRewardLayouts";
import "../Loyalty/styles.scss";
import "./styles.scss";
import { useSubscriptionPlansSelector } from "../../../../redux/actions/app";

interface LoyaltyLeaderboardGiftsEditorProps extends FormikFormChildProps {
  errors: FormikErrors<Config>;
}

const LoyaltyLeaderboardGiftsEditor = (
  props: LoyaltyLeaderboardGiftsEditorProps
) => {
  const { config, setFieldValue } = props;
  const { accountToDisplay } = useAccountToDisplaySelector();
  const subscriptionPlans = useSubscriptionPlansSelector();
  const leaderboardSpecialPrize = config.lty?.leaderboardSpecialPrize as
    | LeaderboardSpecialPrize
    | undefined;

  const hasAdvancedCouponOptions = useMemo(
    () => accountToDisplay?.saasFeatures?.adc || false,
    [accountToDisplay?.saasFeatures?.adc]
  );

  const isPrize = leaderboardSpecialPrize?.type === "prize";
  const prize =
    leaderboardSpecialPrize?.type === "prize"
      ? leaderboardSpecialPrize.prize
      : undefined;

  const handleCustomize = () => {
    setFieldValue("lty.leaderboardSpecialPrize", {
      type: "prize",
      prize: buildDefaultTierPrize(accountToDisplay, subscriptionPlans)
    });
  };

  const handleStandardReward = () => {
    setFieldValue("lty.leaderboardSpecialPrize", {
      type: "tokens",
      amount: DEFAULT_LEADERBOARD_TOKEN_AMOUNT
    });
  };

  const handlePrizeUpdate = (updatedPrize: LoyaltyTierPrize) => {
    setFieldValue("lty.leaderboardSpecialPrize", {
      type: "prize",
      prize: updatedPrize
    });
  };

  const handleGenerateIdeas = async () => {
    const suggestion = (await NiceModal.show(GiftSuggestionModal)) as
      | { name?: string; image?: string }
      | undefined;
    const currentPrize =
      prize ?? buildDefaultTierPrize(accountToDisplay, subscriptionPlans);
    if (suggestion?.name || suggestion?.image) {
      handlePrizeUpdate({
        ...currentPrize,
        ...(suggestion?.name && { name: suggestion.name }),
        ...(suggestion?.image && { image: suggestion.image })
      });
    }
  };

  return (
    <LoyaltyRewardEditorPageLayout
      titleKey="loyalty_customer_of_month_section_title"
      contentClassName="px-4 customer-of-the-month-container"
      style={{ paddingBottom: 100 }}
    >
      <LoyaltyRewardCardLayout
        cardId="leaderboard-prize-card"
        icon={<TwemojiWrapper isBigText={true}>🏆</TwemojiWrapper>}
        subtitle={
          <KC_UIText
            weight={KC_Weights.reg}
            text={tt("loyalty_leaderboard_top_customer_reward_desc")}
            className="text-secondary"
          />
        }
        rightHeader={
          !isPrize && (
            <>
              <KC_UIText
                weight={KC_Weights.bold}
                text={tt("loyalty_tokens_count", {
                  count:
                    leaderboardSpecialPrize?.amount ??
                    DEFAULT_LEADERBOARD_TOKEN_AMOUNT
                })}
              />
              <span className="loyalty-token-coin">
                <TwemojiWrapper>🪙</TwemojiWrapper>
              </span>
            </>
          )
        }
        primaryAction={
          !isPrize && (
            <KC_Button
              size={KC_ButtonSize.Large}
              isPrimary={false}
              isBlack={true}
              showIconOnTheRight={true}
              customIcon={
                <span
                  className="loyalty-button-icon-wrap"
                  style={{ fontSize: 20 }}
                >
                  🎁
                </span>
              }
              text={tt("loyalty_customize_reward")}
              type="button"
              onClick={handleCustomize}
              className="w-100 mt-2"
            />
          )
        }
        cardClassName="loyalty-gift-leaderboard-editor-card"
      >
        {isPrize && prize && (
          <LoyaltyPrizeRewardEditor
            prize={prize}
            onPrizeChange={handlePrizeUpdate}
            hasAdvancedCouponOptions={hasAdvancedCouponOptions}
            onGenerateIdeas={handleGenerateIdeas}
            onOfferStandardReward={handleStandardReward}
            idPrefix="lty.leaderboardSpecialPrize.prize"
          >
            <div className="loyalty-leaderboard-tie-warning mt-2">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-warning me-2"
              />
              <KC_UIText
                weight={KC_Weights.reg}
                text={tt("loyalty_leaderboard_tie_warning")}
                className="text-secondary"
              />
            </div>
          </LoyaltyPrizeRewardEditor>
        )}
      </LoyaltyRewardCardLayout>
    </LoyaltyRewardEditorPageLayout>
  );
};

export default LoyaltyLeaderboardGiftsEditor;
